﻿// @ts-check
import React from "react";
import classnames from "classnames";
import { Link, Navigate } from "react-router";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Form,
  FormGroup,
  Col,
  Label,
  InputGroup,
  InputGroupText,
  Input,
} from "reactstrap";
import Select from "react-select";

import PageTypesUpdateContainer from "../PageTypesUpdateContainer";
import type { PageTypeSelection } from "../PageTypesUpdateContainer";
import HandleContent, { type HandleContentProps } from "./HandleContent";
import VariableField, {
  bindFieldProps,
} from "../../variableFields/VariableField";
import ContentStringField from "../../variableFields/ContentStringField";
import { CONTENT_OVERRIDES } from "../../variableFields/contentOverrides";
import { filterTemplateContent } from "../contentVariableFilter";
import ProductSortControl from "../ProductSortControl";
import type { ProductSortControlProps } from "../ProductSortControl";
import type { RootState } from "@/services/store";
import { resolveContentDeclarations } from "./updatePageHelpers";
import { linkAwareValueChange } from "../contentVariableHelpers";

// UpdatePageView renders only the four basic content types inline; the
// complex types are delegated to HandleContent (both statuses) below, which
// skip these same four (the two lists stay disjoint).
const BASIC_CONTENT_TYPES = ["String", "Number", "Boolean", "Link"];

// The update-Required-only quirk (SCRUM-640 pin): the date input is disabled
// for the two midnightExpress templates; the Optional column never is.
// Module-level so the bound component identity is stable across renders.
const REQUIRED_MIDNIGHT_OVERRIDES = {
  ...CONTENT_OVERRIDES,
  String: bindFieldProps(ContentStringField, { dateDisabled: true }),
};

/** One option of the template select; `value` is the template document. */
type TemplateOption = { label?: string; value?: { name?: string } };

/** An input-style envelope the container's legacy handlers read. */
type ValueEnvelope = { target: { name: string; value: unknown } };

export type UpdatePageViewProps = {
  redirect?: string | null;
  CurrentSite?: unknown;
  /** The container's page save state, `any` there as its JSDoc had it. */
  page: any; // any: the container's untyped page document (UpdatePageContainer.tsx useState<any>)
  /** The page-type pick the container owns (SCRUM-2216); narrows HandleContent's product options. */
  pageTypeSelection: PageTypeSelection;
  onPageTypeChange: (selection: PageTypeSelection) => void;
  showCheckerResult: boolean;
  /** Per-field "is missing" flags from the container's validation. */
  formchecker: {
    globalTxt?: boolean;
    metaDescription?: boolean;
    redirectFrom?: boolean;
    page_configuration?: boolean;
  };
  selectTemplates: TemplateOption[];
  localUsers: { label: unknown; value: unknown }[];
  /** The site's page list as the store holds it (`state.pages.pages`). */
  pages?: RootState["pages"]["pages"];
  onChangeFormInputHandler: React.ChangeEventHandler<HTMLInputElement>;
  onChangeCheckInputHandler: React.ChangeEventHandler<HTMLInputElement>;
  onChangeCheckboxByName: React.ChangeEventHandler<HTMLInputElement>;
  redirectFromText?: string;
  onChangeRedirectFrom: React.ChangeEventHandler<HTMLInputElement>;
  onProductSortChange: ProductSortControlProps["onChange"];
  properties?: ProductSortControlProps["properties"];
  SelectTemplatesInputHandler: (option: TemplateOption | null) => void;
  onChangeContentOptional: HandleContentProps["onChangeContentOptional"];
  onChangeVariableValue: (e: ValueEnvelope) => void;
  handleLinkInputRequired: (e: ValueEnvelope, variableName: string) => void;
  handleRequiredVariableChange: HandleContentProps["onVariableChange"];
  handleOptionalVariableChange: HandleContentProps["onVariableChange"];
  UpdatePageHandler: React.MouseEventHandler<HTMLButtonElement>;
  oldPage?: unknown;
};

const UpdatePageView = ({
  redirect,
  CurrentSite,
  page,
  pageTypeSelection,
  onPageTypeChange,
  showCheckerResult,
  formchecker,
  selectTemplates,
  localUsers,
  pages = [],
  onChangeFormInputHandler,
  onChangeCheckInputHandler,
  onChangeCheckboxByName,
  redirectFromText = "",
  onChangeRedirectFrom,
  onProductSortChange,
  properties = [],
  SelectTemplatesInputHandler,
  onChangeContentOptional,
  onChangeVariableValue,
  handleLinkInputRequired,
  handleRequiredVariableChange,
  handleOptionalVariableChange,
  UpdatePageHandler,
  oldPage,
}: UpdatePageViewProps) => {
  // SCRUM-1514: the container's `page` is the SAVE state and carries a page's own
  // data only — `variable` and `value`. Which input a variable renders as, and
  // which of the two columns it belongs to, is the template's to say, so it is
  // derived here in the presentational half. Resolved HERE and not in the
  // container for the same reason UpdatePageHeaderFooterView does it: what the
  // container holds is what gets stored, and re-persisting the declaration is
  // exactly what this ticket removes.
  //
  // Both memos sit above the `redirect` early return — hooks may not be
  // conditional — and the identity behaviour is load-bearing rather than an
  // optimisation: `HandleContent` (required)/`Optional` run a `setVariables` effect
  // keyed on `page_to_update`, so a fresh object every render would re-enter
  // that effect on every render.
  const resolvedContent = React.useMemo(
    () => resolveContentDeclarations(page?.page_content, page?.page_template),
    [page?.page_content, page?.page_template]
  );
  const resolvedPage = React.useMemo(
    () =>
      resolvedContent === page?.page_content
        ? page
        : { ...page, page_content: resolvedContent },
    [page, resolvedContent]
  );

  if (redirect) {
    return (
      <>
        <Navigate to={redirect} />
      </>
    );
  }

  const dateDisabled =
    page?.page_template?.name === "midnightExpressProduct" ||
    page?.page_template?.name === "midnightExpressListing";
  const requiredOverrides = dateDisabled
    ? REQUIRED_MIDNIGHT_OVERRIDES
    : CONTENT_OVERRIDES;

  // Registry adapter: blocks report (name, value); the container keeps its
  // two legacy channels. Link edits are converted back to the edited half so
  // handleLinkInputRequired's per-half merge contract stays intact.
  const valueChangeFor = (contentVar) =>
    linkAwareValueChange(contentVar, handleLinkInputRequired, (name, value) =>
      onChangeVariableValue({ target: { name, value } })
    );

  const selectedTemplateOption = selectTemplates.find(
    (opt) => opt.value?.name === page?.page_template?.name
  );

  return (
    <>
      <SimpleHeader name="Update page" parentName="Pages" />
      <Container className="mt--6" fluid>
        <Card>
          <CardHeader className="text-center">
            <h3 className="mb-0">Updating page</h3>
          </CardHeader>
          <CardBody className="text-center">
            {CurrentSite !== undefined ? (
              <Form>
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Global title
                  </Label>
                  <Col md="10">
                    <InputGroup
                      className={classnames("input-group-merge", {
                        focused: page?.global_title,
                      })}
                    >
                      <InputGroupText>
                        <i className="ni ni-world-2 text-primary" />
                      </InputGroupText>
                      <Input
                        placeholder="Insert Global title"
                        type="text"
                        value={page?.global_title || ""}
                        onChange={onChangeFormInputHandler}
                        name="global_title"
                        required
                      />
                    </InputGroup>
                    {showCheckerResult && formchecker.globalTxt ? (
                      <div className="text-danger">
                        Please insert page global title
                      </div>
                    ) : null}
                    {showCheckerResult && !formchecker.globalTxt ? (
                      <div className="text-success">Looks good!</div>
                    ) : null}
                  </Col>
                </FormGroup>

                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    META description
                  </Label>
                  <Col md="10">
                    <InputGroup
                      className={classnames("input-group-merge", {
                        focused: page?.meta_description,
                      })}
                    >
                      <InputGroupText>
                        <i className="ni ni-collection text-primary" />
                      </InputGroupText>
                      <Input
                        placeholder="Insert META description"
                        rows="3"
                        type="textarea"
                        value={page?.meta_description || ""}
                        onChange={onChangeFormInputHandler}
                        name="meta_description"
                        required
                      />
                    </InputGroup>
                    {showCheckerResult && formchecker.metaDescription ? (
                      <div className="text-danger">
                        Please insert page meta description
                      </div>
                    ) : null}
                    {showCheckerResult && !formchecker.metaDescription ? (
                      <div className="text-success">Looks good!</div>
                    ) : null}
                  </Col>
                </FormGroup>

                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Robots / Crawler tag
                  </Label>
                  <Col md="10">
                    <Input
                      type="select"
                      name="robots"
                      value={page?.robots ?? ""}
                      onChange={onChangeFormInputHandler}
                    >
                      <option value="">Default (index, follow)</option>
                      <option value="noindex">No index</option>
                      <option value="nofollow">No follow</option>
                      <option value="noindex,nofollow">No index, no follow</option>
                    </Input>
                  </Col>
                </FormGroup>

                {/* SCRUM-1587: the editor's half of page redirects. The site
                    answers 308 from each of these paths to this page. */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2" htmlFor="redirect_from">
                    Redirect from (old paths)
                  </Label>
                  <Col md="10">
                    <Input
                      type="textarea"
                      id="redirect_from"
                      name="redirect_from"
                      rows="3"
                      placeholder={"old/path/\nanother-old/path/"}
                      value={redirectFromText}
                      onChange={onChangeRedirectFrom}
                      invalid={showCheckerResult && formchecker.redirectFrom}
                    />
                    <small className="form-text text-muted">
                      One path per line. Visitors of these old paths are sent to
                      this page. Saved without a leading slash and with a
                      trailing slash, like the page path itself.
                    </small>
                    {showCheckerResult && formchecker.redirectFrom ? (
                      <div className="text-danger">
                        A page cannot redirect from its own path.
                      </div>
                    ) : null}
                  </Col>
                </FormGroup>

                {/* SCRUM-1362. Uses onChangeCheckboxByName, not the
                    sub_pages-only handler further down. */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2" htmlFor="skip_product_page">
                    Skip this product page
                  </Label>
                  <Col md="10">
                    <label className="custom-toggle custom-toggle-danger mr-1">
                      <input
                        id="skip_product_page"
                        name="skip_product_page"
                        type="checkbox"
                        checked={!!page?.skip_product_page}
                        onChange={onChangeCheckboxByName}
                      />
                      <span
                        className="custom-toggle-slider rounded-circle"
                        data-label-off="No"
                        data-label-on="Yes"
                      />
                    </label>
                    <small className="form-text text-muted">
                      Sends visitors straight to the provider instead of showing
                      this product page. The page keeps its content.
                    </small>
                  </Col>
                </FormGroup>

                {page?.page_configuration?.type === "sub_category" ? (
                  <ProductSortControl
                    productSort={page?.product_sort}
                    properties={properties}
                    onChange={onProductSortChange}
                  />
                ) : null}

                <h3 className="mb-0 text-center">Selected Page Type</h3>
                {showCheckerResult && formchecker.page_configuration ? (
                  <div className="text-danger">
                    Please finish page type configuration
                  </div>
                ) : null}
                {showCheckerResult && !formchecker.page_configuration ? (
                  <div className="text-success">Looks good!</div>
                ) : null}

                <hr />

                {/* pageId keys the hydration latch: this screen's route is not
                    remounted when :id changes, so the container needs to know
                    which page it hydrated for (SCRUM-1466 round 1, lens B). */}
                <PageTypesUpdateContainer
                  updateform={true}
                  pageId={page?._id}
                  page_configuration={page?.page_configuration}
                  onPageTypeChange={onPageTypeChange}
                />

                <hr />

                {/* SELECT TEMPLATE */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Design Template
                  </Label>
                  <Col md="10">
                    <Select
                      value={selectedTemplateOption || null}
                      name="template"
                      options={selectTemplates}
                      onChange={SelectTemplatesInputHandler}
                      isClearable
                      isSearchable
                      required
                    />
                  </Col>

                  {(page?.page_template?.content_variables || page?.page_content?.length > 0) ? (
                    <>
                      <Col md="12" style={{ marginTop: "15px" }}>
                        <center>
                          <h3
                            className="mb-0 text-center"
                            style={{
                              borderBottom: "1px solid #d0d2d6",
                              lineHeight: "0.1em",
                              margin: "10px 0 20px",
                            }}
                          >
                            <span style={{ background: "#fff", padding: "0 10px" }}>
                              Template Variables Configuration
                            </span>
                          </h3>
                        </center>
                      </Col>

                      {/* REQUIRED CONTENT VARS */}
                      <FormGroup className="col-md-6" style={{ marginTop: "20px" }}>
                        <Col md="10">
                          {filterTemplateContent(
                            resolvedContent,
                            page?.page_template?.content_variables?.ContentRequired,
                            "ContentRequired"
                          ).map((contentVar) =>
                            BASIC_CONTENT_TYPES.includes(contentVar.type) ? (
                              <VariableField
                                key={contentVar.variable}
                                settingVar={contentVar}
                                options={localUsers}
                                onChange={valueChangeFor(contentVar)}
                                overrides={requiredOverrides}
                                fieldProps={{ pages }}
                              />
                            ) : null
                          )}
                          <HandleContent status="required"
                            page_to_update={resolvedPage}
                            pageType={pageTypeSelection}
                            onVariableChange={handleRequiredVariableChange}
                          />
                        </Col>
                      </FormGroup>

                      <FormGroup className="col-md-6" style={{ marginTop: "20px" }}>
                        <Col md="10">
                          {filterTemplateContent(
                            resolvedContent,
                            page?.page_template?.content_variables?.ContentOptional,
                            "ContentOptional"
                          ).map((contentVar) =>
                            BASIC_CONTENT_TYPES.includes(contentVar.type) ? (
                              <VariableField
                                key={contentVar.variable}
                                settingVar={contentVar}
                                options={localUsers}
                                onChange={valueChangeFor(contentVar)}
                                overrides={CONTENT_OVERRIDES}
                                fieldProps={{ pages }}
                              />
                            ) : null
                          )}

                          <HandleContent status="optional" page_to_update={resolvedPage} pageType={pageTypeSelection} onChangeContentOptional={onChangeContentOptional} onVariableChange={handleOptionalVariableChange} />
                        </Col>
                      </FormGroup>
                    </>
                  ) : null}
                </FormGroup>
                <FormGroup className="row">
                  <label className="form-control-label " htmlFor="exampleFormControlSelect3">
                    Including navigation and footer pages
                  </label>
                  <Col md="1">
                    <label className="custom-toggle custom-toggle-danger mr-1">
                      <input
                        checked={!!page?.sub_pages}
                        onChange={onChangeCheckInputHandler}
                        name="enabled"
                        type="checkbox"
                      />
                      <span
                        className="custom-toggle-slider rounded-circle"
                        data-label-off="No"
                        data-label-on="Yes"
                      />
                    </label>
                  </Col>
                </FormGroup>

                <button
                  className="btn btn-outline-primary mr-1 float-right"
                  type="submit"
                  onClick={UpdatePageHandler}
                >
                  Finish Updating
                </button>
                <Link to="/admin/display-pages" className="btn btn-outline-default mr-1 float-left">
                  Back To Pages
                </Link>
              </Form>
            ) : (
              <Link to="/admin/display-pages" className="btn btn-outline-default mr-1 float-left">
                Back To Pages
              </Link>
            )}
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default UpdatePageView;
